import type { DossierResponse } from '@/features/dossier/api';
import { DossierProvenanceHeader } from '@/features/dossier/components/DossierResultRenderer';

// ── UK class label ──────────────────────────────────────────────────────────

/**
 * UK device class label — UK MDR 2002 classes are IIa / IIb / III.
 * Accepts both the raw class token (e.g. "IIa") and the prefixed form
 * used as submission_type (e.g. "uk-class-iia"). Never carries "MDR" or
 * "Health Canada" framing — UK-only.
 */
export function ukClassLabel(t: string | null | undefined): string {
  if (!t) return '';
  // Strip optional "uk-class-" prefix, then normalise case.
  const raw = t.replace(/^uk-class-/i, '');
  // Accepted classes: iia → IIa, iib → IIb, iii → III
  const norm = raw.toUpperCase();
  if (norm === 'IIA') return 'Class IIa';
  if (norm === 'IIB') return 'Class IIb';
  if (norm === 'III') return 'Class III';
  return t; // Passthrough for unrecognised values.
}

// ── UK MHRA submission provenance header ──────────────────────────────────────

/** The UKCA submission header — now the shared {@link DossierProvenanceHeader}
 *  (device · type · pathway · generated · by). The UK applicant identity (UK
 *  Responsible Person · UK Approved Body · UKCA marking · signatory · contact) is
 *  account-sourced and lives in the generated PDF's submission body, not here. */
export function UkDossierHeader({ response }: { response: DossierResponse }) {
  return (
    <DossierProvenanceHeader
      response={response}
      eyebrow="UK MHRA Submission Dossier"
      pathwayLabel={ukClassLabel(response.submission_type)}
    />
  );
}
